<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import instagramClient from 'dashboard/api/channel/instagramClient';
import Button from 'dashboard/components-next/button/Button.vue';
import InstagramFacebookLogin from './instagram/InstagramFacebookLogin.vue';

const { t } = useI18n();
const router = useRouter();

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);
const authMode = ref('choose');

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t(
        'INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.ERROR_AUTH'
      );
    } else {
      errorStateMessage.value = t(
        'INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.ERROR_MESSAGE'
      );
      errorStateDescription.value = errorMessage;
    }
  }

  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const response = await instagramClient.generateAuthorization({
    channel_type: 'comments',
  });
  const {
    data: { url },
  } = response;

  window.location.href = url;
};

const onFacebookChannelCreated = data => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: data.id },
  });
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <InstagramFacebookLogin
        v-else-if="authMode === 'facebook'"
        channel-type="comments"
        @back="authMode = 'choose'"
        @created="onFacebookChannelCreated"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{
            $t(
              'INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.CONNECT_YOUR_INSTAGRAM_PROFILE'
            )
          }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.HELP') }}
        </p>
        <div class="flex flex-col items-center gap-4 w-full max-w-sm">
          <Button
            class="w-full text-white !rounded-full !px-6 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]"
            lg
            icon="i-ri-instagram-line"
            :disabled="isRequestingAuthorization"
            :is-loading="isRequestingAuthorization"
            :label="
              $t('INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.CONTINUE_WITH_INSTAGRAM')
            "
            @click="requestAuthorization()"
          />
          <Button
            class="w-full"
            lg
            icon="i-ri-facebook-circle-line"
            :label="
              $t('INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.CONTINUE_WITH_FACEBOOK')
            "
            @click="authMode = 'facebook'"
          />
        </div>
      </div>
    </div>
  </div>
</template>
